import { useState } from "react";
import { DataTable, LoadingBlock, Pill, Section, useAdminResource } from "./AdminUI";
import { formatDateTimeSeconds, timeAgo } from "../../lib/datetime";

const PAGE = 50;

export default function AuditPanel() {
  const [action, setAction] = useState("");
  const [highRiskOnly, setHighRiskOnly] = useState(false);
  const [skip, setSkip] = useState(0);
  const { data, loading, error } = useAdminResource("/admin/audit", {
    params: { action, high_risk_only: highRiskOnly, limit: PAGE, skip },
  });

  const total = data?.total ?? 0;

  return (
    <Section
      id="audit"
      title="// AUDIT TRAIL"
      actions={<span className="text-2xs uppercase tracking-widest text-ink-muted tabular-nums">{total} entr{total === 1 ? "y" : "ies"}</span>}
    >
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="brutal-label" htmlFor="audit-action">Action</label>
          <select id="audit-action" className="brutal-select" value={action}
                  onChange={(e) => { setSkip(0); setAction(e.target.value); }} data-testid="audit-action">
            <option value="">All actions</option>
            {(data?.known_actions || []).map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm pb-2" htmlFor="audit-high-risk">
          <input id="audit-high-risk" type="checkbox" checked={highRiskOnly}
                 onChange={(e) => { setSkip(0); setHighRiskOnly(e.target.checked); }}
                 data-testid="audit-high-risk" />
          High-risk only
        </label>
      </div>

      {loading && <LoadingBlock label="Loading audit trail" />}
      {error && <div className="brutal-card p-6 text-ink-accent text-sm" role="alert">{error}</div>}

      {!loading && !error && (
        <>
          <DataTable
            testid="admin-audit-table"
            caption="Privileged actions, newest first"
            empty="Nothing recorded yet. Entries appear here as soon as an admin changes a plan, suspends an account, or grants access."
            columns={[{ key: "at", label: "When" }, { key: "actor", label: "Admin" },
                      { key: "action", label: "Action" }, { key: "target", label: "Target" },
                      { key: "detail", label: "Detail" }, { key: "ip", label: "IP" }]}
            rows={data?.entries}
            renderRow={(a) => (
              <tr key={a.audit_id} className="border-t border-rule align-top">
                <td className="p-3 font-mono-ui text-xs whitespace-nowrap">
                  {formatDateTimeSeconds(a.at)}
                  <div className="text-xs text-ink-subtle">{timeAgo(a.at)}</div>
                </td>
                <td className="p-3 font-mono-ui text-xs break-all">{a.actor}</td>
                <td className="p-3">
                  <Pill tone={a.high_risk ? "danger" : "default"}>{a.action}</Pill>
                </td>
                <td className="p-3 font-mono-ui text-xs text-ink-muted break-all">
                  {a.detail?.email || a.target_id || "—"}
                </td>
                <td className="p-3 text-xs text-ink-muted max-w-xs">
                  {Object.entries(a.detail || {})
                    .filter(([k]) => k !== "email")
                    .map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`)
                    .join("  ") || "—"}
                </td>
                <td className="p-3 font-mono-ui text-xs text-ink-subtle whitespace-nowrap">{a.ip || "—"}</td>
              </tr>
            )}
          />
          <div className="flex items-center justify-between gap-3 mt-3">
            <button type="button" className="brutal-btn brutal-btn-sm brutal-btn-secondary"
                    disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))}>← Previous</button>
            <span className="text-2xs uppercase tracking-widest text-ink-muted tabular-nums">
              {total === 0 ? "0" : `${skip + 1}–${Math.min(skip + PAGE, total)} of ${total}`}
            </span>
            <button type="button" className="brutal-btn brutal-btn-sm brutal-btn-secondary"
                    disabled={skip + PAGE >= total} onClick={() => setSkip(skip + PAGE)}>Next →</button>
          </div>
        </>
      )}
    </Section>
  );
}
